/**
 * The read-only display of a document's stored tags — every kind of document
 * (LB6d-T1, decision 60: one tag store, for files and articles alike).
 *
 * Drawn by the two readers: the file / synced document detail page
 * (routes/library/doc.$docId.tsx, through DocumentTagsCard while not editing)
 * and the article reader (routes/library/article.$articleId.tsx). It renders
 * the `tags` the reader's own GET answered, in the order the server gave them,
 * and nothing else:
 *   - it asks no permission key — the GET that answered the detail is the
 *     gate, and a second gate here could only hide what the server answered;
 *   - it reads no catalogue and makes no request;
 *   - it offers no control.
 * No tags → nothing is drawn (no empty card, no "No tags").
 *
 * Its editing counterpart is the ONE tag field, document-tags-field.tsx.
 */
import { Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ArticleTagShape } from '@argusai/shared';

export function DocumentTagsValue({ tags }: { tags: readonly ArticleTagShape[] }) {
  if (tags.length === 0) return null;
  return (
    <Card data-testid="document-tags-value">
      <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-1"><Tag className="h-3.5 w-3.5" />Tags</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant="outline"
              className="text-xs"
              style={tag.color ? { borderColor: tag.color, color: tag.color } : undefined}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
